import { NODE_TYPE_LABELS, summarizeNode } from "@velizhanin-dev/bot-scenario"
import type { DraftNode, ScenarioDraft } from "@/entities/bot-builder"
import { orderFlowNodes } from "@/entities/bot-builder"
import type { FlowStats } from "@/entities/bot-stats"
import { cn } from "@/shared/lib/utils"
import { Card } from "@/shared/ui/card"
import { EmptyState } from "@/shared/ui/empty-state"

export type StepMode = "passed" | "stuck"

export interface StepSelection {
	nodeId: string
	mode: StepMode
}

/** Шаги воронки метки: сколько людей из метки дошли до каждого блока и где стоят сейчас. */
export function UtmFunnelSteps({
	draft,
	flowId,
	stats,
	total,
	selected,
	onSelect,
}: {
	draft: ScenarioDraft
	flowId: string
	stats: FlowStats | undefined
	total: number
	selected: StepSelection | null
	onSelect: (selection: StepSelection | null) => void
}) {
	const byNode = new Map((stats?.nodes ?? []).map((row) => [row.nodeId, row]))
	const steps = orderFlowNodes(draft, flowId).ordered.filter((node) => node.type !== "start")
	const scale = Math.max(total, ...steps.map((node) => byNode.get(node.id)?.passed ?? 0), 1)

	if (steps.length === 0) {
		return <EmptyState title="В воронке пока нет блоков">Добавьте блоки в конструкторе.</EmptyState>
	}

	return (
		<Card className="overflow-hidden">
			<div
				aria-hidden
				className="hidden gap-x-4 border-b border-border bg-secondary/30 px-4 py-2 text-[11px] font-medium tracking-wide text-muted-foreground uppercase sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)_6.5rem_6.5rem_4.5rem]"
			>
				<span>Шаг</span>
				<span>Доля от пришедших</span>
				<span className="text-right">Прошли</span>
				<span className="text-right">Стоят</span>
				<span className="text-right">Ушли</span>
			</div>
			<div className="divide-y divide-border">
			{steps.map((node, index) => {
				const row = byNode.get(node.id)
				const passed = row?.passed ?? 0
				const stuck = row?.stuck ?? 0
				const isPassed = selected?.nodeId === node.id && selected.mode === "passed"
				const isStuck = selected?.nodeId === node.id && selected.mode === "stuck"
				const prev = index > 0 ? (byNode.get(steps[index - 1]?.id ?? "")?.passed ?? 0) : total
				const lost = Math.max(prev - passed, 0)
				return (
					<div
						key={node.id}
						className={cn(
							"grid items-center gap-x-4 gap-y-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)_6.5rem_6.5rem_4.5rem]",
							(isPassed || isStuck) && "bg-primary/5",
						)}
					>
						<div className="min-w-0">
							<p className="truncate text-sm font-medium">{stepTitle(node, draft)}</p>
							<p className="text-xs text-muted-foreground">{NODE_TYPE_LABELS[node.type as never]}</p>
						</div>
						<div className="flex items-center gap-3">
							<div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
								<div
									className="h-full rounded-full bg-primary transition-[width]"
									style={{ width: `${Math.round((passed / scale) * 100)}%` }}
								/>
							</div>
							<span className="w-10 text-right text-xs text-muted-foreground tabular-nums">
								{total > 0 ? `${Math.round((passed / total) * 100)}%` : "–"}
							</span>
						</div>
						<div className="flex sm:justify-end">
							<StepButton
								active={isPassed}
								disabled={passed === 0}
								onClick={() => onSelect(isPassed ? null : { nodeId: node.id, mode: "passed" })}
							>
								<b className="tabular-nums">{passed}</b> <span className="sm:hidden">прошли</span>
							</StepButton>
						</div>
						<div className="flex sm:justify-end">
							{stuck > 0 ? (
								<StepButton
									tone="warning"
									active={isStuck}
									onClick={() => onSelect(isStuck ? null : { nodeId: node.id, mode: "stuck" })}
								>
									<b className="tabular-nums">{stuck}</b> <span className="sm:hidden">стоят</span>
								</StepButton>
							) : (
								<span className="px-2.5 py-1 text-xs text-muted-foreground/60">0</span>
							)}
						</div>
						<span className="text-right text-xs text-muted-foreground tabular-nums">
							{lost > 0 && index > 0 ? `−${lost}` : "–"}
						</span>
					</div>
				)
			})}
			</div>
		</Card>
	)
}

function StepButton({
	active,
	disabled,
	tone,
	onClick,
	children,
}: {
	active: boolean
	disabled?: boolean
	tone?: "warning"
	onClick: () => void
	children: React.ReactNode
}) {
	return (
		<button
			type="button"
			disabled={disabled}
			aria-pressed={active}
			onClick={onClick}
			className={cn(
				"min-w-14 rounded-full border px-2.5 py-1 text-center text-xs transition-colors disabled:cursor-default disabled:opacity-50",
				tone === "warning"
					? "border-amber-400/30 text-amber-300 hover:bg-amber-400/10"
					: "border-border hover:bg-accent",
				active && "border-primary bg-primary/10 text-foreground",
			)}
		>
			{children}
		</button>
	)
}

export function stepTitle(node: DraftNode, draft: ScenarioDraft): string {
	const text = summarizeNode(node, draft.flows, draft.tags).replace(/\s+/g, " ").trim()
	const label = NODE_TYPE_LABELS[node.type as never]
	if (!text) return label
	if (/^-?\d+$/.test(text)) return `${label}: канал ${text}`
	return text.length > 70 ? `${text.slice(0, 70)}…` : text
}
